import React, { useState, useMemo, useCallback, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import AuthenticatedLayout from '../../components/layouts/authenticated_layout'
import EnterpriseSearchForm from './components/enterprise_search_form'
import { useEnterpriseSearch } from '../../hooks/use_enterprise_search'
import RadioButtonGroup from '../../components/buttons/radio_button_group'
import LoadingSpinner from '../../components/loading/loading_spinner'
import EnterpriseCard from '../../components/cards/enterprise_card'
import { useForm } from 'react-hook-form'
import { useInfiniteScroll } from '../../hooks/use_infinite_scroll'
import type { RadioButtonType } from '../../components/buttons/radio_button_group'
import {
  searchParamsToURLParams,
  urlParamsToSearchParams,
  getSortFromURL,
  setSortToURL,
} from './utils/url_params'
import {
  EnterpriseSortType,
  EnterpriseSortTypeLabel,
  EnterpriseSortTypeValue,
} from '../../types/enterprise'
import Breadcrumb from '../../components/common/breadcrumb'

export interface SearchParams {
  keyword: string
  enterpriseCategory: string
  businessCategory: string
  location: string
  companySize: string
  capital: string
  sdgsCategories: string[]
}

const EnterprisesSearchPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [formShow, setFormShow] = useState(true)

  // URLからの初期ソート値を取得
  // TODO: マッチング率ソートはパフォーマンス改善後に有効化
  // EnterpriseSortType.MATCHING_RATE
  const initialSort =
    (getSortFromURL(searchParams) as EnterpriseSortTypeValue) ||
    EnterpriseSortType.CREATED_AT

  // URLから初期検索パラメータを取得
  const initialSearchParams = useMemo(() => {
    return urlParamsToSearchParams(searchParams)
  }, [])

  const {
    enterprises,
    isLoading,
    isLoadingMore,
    hasMore,
    searchEnterprises,
    loadMore,
    changeSort,
    currentSort,
  } = useEnterpriseSearch(9, initialSort, initialSearchParams)

  const { LoadingElement } = useInfiniteScroll(loadMore, isLoadingMore, hasMore)

  // フォームの状態管理
  const { register, handleSubmit, watch, setValue, getValues, reset } =
    useForm<SearchParams>({
      defaultValues: initialSearchParams,
      mode: 'onSubmit',
    })

  // 初期表示時にURLパラメータがあればフォームを閉じる
  useEffect(() => {
    const hasUrlParams = Array.from(searchParams.keys()).some(
      (key) => key !== 'sort' && searchParams.get(key)
    )
    if (hasUrlParams) {
      setFormShow(false)
    }
  }, [])

  const handleSortChange = useCallback(
    (type: EnterpriseSortTypeValue) => {
      if (type !== currentSort) {
        changeSort(type)
        // URLにソートパラメータを追加（履歴を置き換える）
        setSearchParams(
          (prev) => {
            const newParams = new URLSearchParams(prev)
            setSortToURL(newParams, type)
            return newParams
          },
          { replace: true }
        )
      }
    },
    [currentSort, changeSort, setSearchParams]
  )

  const sortButtons: RadioButtonType[] = useMemo(
    () => [
      // TODO: マッチング率ソートはパフォーマンス改善後に有効化
      // {
      //   label: EnterpriseSortTypeLabel[EnterpriseSortType.MATCHING_RATE],
      //   onClick: () => handleSortChange(EnterpriseSortType.MATCHING_RATE),
      // },
      {
        label: EnterpriseSortTypeLabel[EnterpriseSortType.CREATED_AT],
        onClick: () => handleSortChange(EnterpriseSortType.CREATED_AT),
      },
      {
        label: EnterpriseSortTypeLabel[EnterpriseSortType.NAME],
        onClick: () => handleSortChange(EnterpriseSortType.NAME),
      },
    ],
    [handleSortChange]
  )

  const handleSearch = (params: SearchParams) => {
    // 検索パラメータを使用してAPIを呼び出し
    searchEnterprises(params)
    // 検索実行時にフォームを閉じる
    setFormShow(false)

    // URLパラメータを更新（履歴を置き換える）
    const newURLParams = searchParamsToURLParams(params)
    // 現在のソート設定を保持
    if (currentSort) {
      setSortToURL(newURLParams, currentSort)
    }
    setSearchParams(newURLParams, { replace: true })
  }

  const toggleForm = () => {
    setFormShow(!formShow)
  }

  return (
    <AuthenticatedLayout>
      <div className="container mx-auto px-4 py-8">
        <Breadcrumb
          items={[
            { label: 'ホーム', path: '/dashboard' },
            { label: '企業検索' },
          ]}
        />

        {/* ヘッダー */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold mb-2 text-main-black">
            企業詳細検索
          </h1>
          <p className="text-main-darkgray">
            SDGsマッチングに参加している企業を検索できます。条件を指定して、あなたの企業とマッチする企業を見つけましょう。
          </p>
        </div>

        {/* 検索フォームの開閉UI */}
        <div className="mb-8">
          <div className="w-full rounded-lg bg-main-white py-6 px-4 shadow-md">
            {/* フォーム本体 */}
            <div
              className="overflow-hidden transition-all duration-300"
              style={formShow ? { height: 'auto' } : { height: 0 }}
            >
              <EnterpriseSearchForm
                register={register}
                watch={watch}
                setValue={setValue}
                getValues={getValues}
                handleSearch={handleSearch}
                handleSubmit={handleSubmit}
                toggleForm={toggleForm}
                reset={() => {
                  reset()
                  // URLパラメータをクリア（ソートは保持、履歴を置き換える）
                  setSearchParams(
                    (prev) => {
                      const newParams = new URLSearchParams()
                      const currentSortParam = prev.get('sort')
                      if (currentSortParam) {
                        newParams.set('sort', currentSortParam)
                      }
                      return newParams
                    },
                    { replace: true }
                  )
                  // 空の検索を実行（全件表示）
                  searchEnterprises({
                    keyword: '',
                    enterpriseCategory: '',
                    businessCategory: '',
                    location: '',
                    companySize: '',
                    capital: '',
                    sdgsCategories: [],
                  })
                }}
              />
            </div>
            {/* 開閉トグル */}
            <div
              className="flex justify-between overflow-hidden cursor-pointer transition-all duration-300"
              style={formShow ? { height: 0 } : { height: 'auto' }}
              onClick={toggleForm}
            >
              <p className="font-bold text-main-black">
                検索したい項目を選択してください。
              </p>
              <div className="flex flex-col justify-center">
                <span className="material-icons text-main-darkgray">
                  {formShow ? 'expand_less' : 'expand_more'}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* 検索結果（下部） */}
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold text-main-black">検索結果一覧</h2>
          <RadioButtonGroup buttons={sortButtons} />
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
          {isLoading && enterprises.length === 0 ? (
            <div className="flex justify-center py-20 col-span-full">
              <LoadingSpinner />
            </div>
          ) : enterprises.length === 0 ? (
            <div className="col-span-full">
              <div className="text-center py-8 text-main-darkgray">
                検索条件に一致する企業がありません。
              </div>
            </div>
          ) : (
            <>
              {enterprises.map((ent, index) => (
                <EnterpriseCard
                  key={`${ent.id}-${index}`}
                  enterprise={ent}
                  initialized={!isLoading}
                />
              ))}
            </>
          )}

          {enterprises.length > 0 && hasMore && LoadingElement}
        </div>
      </div>
    </AuthenticatedLayout>
  )
}

export default EnterprisesSearchPage
